<template>
  <div class="pipeline-canvas-container">
    <!-- Toolbar -->
    <div class="canvas-toolbar">
      <div class="toolbar-left">
        <input
          v-model="pipelineName"
          @blur="updatePipelineName"
          class="pipeline-name-input"
          placeholder="Enter pipeline name..."
        />
        <span v-if="isDirty" class="unsaved-badge">Unsaved changes</span>
      </div>

      <div class="toolbar-center">
        <div class="node-palette">
          <button @click="addNode('source')" class="palette-btn">
            <Database :size="16" />
            <span>Source</span>
          </button>
          <button @click="addNode('transform')" class="palette-btn">
            <Zap :size="16" />
            <span>Transform</span>
          </button>
          <button @click="addNode('destination')" class="palette-btn">
            <Target :size="16" />
            <span>Destination</span>
          </button>
        </div>
      </div>

      <div class="toolbar-right">
        <button @click="fitView" class="tool-btn">
          <Maximize2 :size="16" />
        </button>
        <button
          @click="savePipeline"
          class="tool-btn primary"
          :disabled="saveDisabled"
          :title="saveDisabled ? 'Editing existing pipelines is not yet supported' : 'Save Pipeline'"
        >
          <Save :size="16" />
          Save Pipeline
        </button>
      </div>
    </div>

    <!-- Vue Flow Canvas -->
    <VueFlow
      v-model:nodes="nodes"
      v-model:edges="edges"
      :default-zoom="1"
      :min-zoom="0.2"
      :max-zoom="4"
      @node-click="onNodeClick"
      @edge-click="onEdgeClick"
      class="vue-flow-wrapper"
    >
      <Background pattern-color="#aaa" :gap="16" />
      <Controls />
      <MiniMap />

      <!-- Custom Node Templates -->
      <template #node-source="props">
        <SourceNode v-bind="props" @delete="deleteNode(props.id)" />
      </template>

      <template #node-transform="props">
        <TransformNode v-bind="props" @delete="deleteNode(props.id)" />
      </template>

      <template #node-destination="props">
        <DestinationNode v-bind="props" @delete="deleteNode(props.id)" />
      </template>
    </VueFlow>

    <!-- Configuration Panel -->
    <transition name="slide-panel">
      <component
        v-if="selectedNode"
        :is="getConfigPanelComponent(selectedNode)"
        :node="selectedNode"
        :source-node="getSourceNode()"
        :transform-node="getTransformNode()"
        :available-connections="availableConnections"
        @update="updateNodeData"
        @close="selectedNode = null"
      />
    </transition>
  </div>
</template>

<script setup>
// @ts-nocheck - Complex Vue Flow component, will be properly typed later
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { VueFlow, useVueFlow } from "@vue-flow/core";
import { Background } from "@vue-flow/background";
import { Controls } from "@vue-flow/controls";
import { MiniMap } from "@vue-flow/minimap";
import { Database, Zap, Target, Save, Maximize2 } from "lucide-vue-next";

import SourceNode from "./nodes/SourceNode.vue";
import TransformNode from "./nodes/TransformNode.vue";
import DestinationNode from "./nodes/DestinationNode.vue";

import SourceConfigPanel from "./nodes/panels/SourceConfigPanel.vue";
import TransformConfigPanel from "./nodes/panels/TransformConfigPanel.vue";
import DestinationConfigPanel from "./nodes/panels/DestinationConfigPanel.vue";

const props = defineProps({
  pipelineId: String,
  initialPipeline: Object,
  saveDisabled: { type: Boolean, default: false },
  availableConnections: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["save", "update-name"]);

const { fitView: vueFlowFitView } = useVueFlow();

// State
const pipelineName = ref("New Pipeline");
const nodes = ref([]);
const edges = ref([]);
const selectedNode = ref(null);
const isDirty = ref(false);

let nodeIdCounter = 0;
let _initDone = false;

// Mark dirty on any node/edge mutation after init
watch(nodes, () => { if (_initDone) isDirty.value = true }, { deep: true });
watch(edges, () => { if (_initDone) isDirty.value = true }, { deep: true });
watch(pipelineName, () => { if (_initDone) isDirty.value = true });

// Initialize
onMounted(async () => {
  if (props.initialPipeline) {
    loadPipelineData(props.initialPipeline);
  } else {
    createDefaultPipeline();
  }
  // Allow watchers to settle before tracking dirty state
  await nextTick();
  _initDone = true;
});

const createDefaultPipeline = () => {
  const sourceId = `source-${++nodeIdCounter}`;
  const transformId = `transform-${++nodeIdCounter}`;
  const destinationId = `destination-${++nodeIdCounter}`;

  nodes.value = [
    {
      id: sourceId,
      type: "source",
      position: { x: 100, y: 200 },
      data: {
        label: "Data Source",
        connectionId: null,
        endpoint: null,
      },
    },
    {
      id: transformId,
      type: "transform",
      position: { x: 500, y: 200 },
      data: {
        label: "Transform",
        columnMappings: [],
      },
    },
    {
      id: destinationId,
      type: "destination",
      position: { x: 900, y: 200 },
      data: {
        label: "Destination",
        tableName: "",
      },
    },
  ];

  edges.value = [
    {
      id: `e${sourceId}-${transformId}`,
      source: sourceId,
      target: transformId,
      animated: true,
      style: { stroke: "#667eea", strokeWidth: 2 },
    },
    {
      id: `e${transformId}-${destinationId}`,
      source: transformId,
      target: destinationId,
      animated: true,
      style: { stroke: "#667eea", strokeWidth: 2 },
    },
  ];
};

const loadPipelineData = (pipeline) => {
  pipelineName.value = pipeline.name;

  const sourceId = "source-1";
  const transformId = "transform-1";
  const destinationId = "destination-1";

  nodes.value = [
    {
      id: sourceId,
      type: "source",
      position: { x: 100, y: 200 },
      data: {
        label: "Data Source",
        connectionId: pipeline.connectionId,
        endpoint: pipeline.config?.extract?.endpoint,
      },
    },
    {
      id: transformId,
      type: "transform",
      position: { x: 500, y: 200 },
      data: {
        label: "Transform",
        columnMappings: pipeline.config?.transform?.columnMappings || [],
      },
    },
    {
      id: destinationId,
      type: "destination",
      position: { x: 900, y: 200 },
      data: {
        label: "Destination",
        tableName: pipeline.config?.load?.tableName,
        writeMode: pipeline.config?.load?.writeMode,
      },
    },
  ];

  edges.value = [
    {
      id: `e${sourceId}-${transformId}`,
      source: sourceId,
      target: transformId,
      animated: true,
      style: { stroke: "#667eea", strokeWidth: 2 },
    },
    {
      id: `e${transformId}-${destinationId}`,
      source: transformId,
      target: destinationId,
      animated: true,
      style: { stroke: "#667eea", strokeWidth: 2 },
    },
  ];
};

const addNode = (type) => {
  const id = `${type}-${++nodeIdCounter}`;

  const newNode = {
    id,
    type,
    position: {
      x: 250 + Math.random() * 200,
      y: 100 + Math.random() * 200,
    },
    data: {
      label: type.charAt(0).toUpperCase() + type.slice(1),
    },
  };

  nodes.value.push(newNode);
};

const deleteNode = (nodeId) => {
  nodes.value = nodes.value.filter((n) => n.id !== nodeId);
  edges.value = edges.value.filter((e) => e.source !== nodeId && e.target !== nodeId);

  if (selectedNode.value?.id === nodeId) {
    selectedNode.value = null;
  }
};

const onNodeClick = ({ node }) => {
  selectedNode.value = node;
};

const onEdgeClick = ({ edge }) => {
  // Could show edge configuration here
  console.log("Edge clicked:", edge);
};

const updateNodeData = (data) => {
  if (selectedNode.value) {
    const nodeIndex = nodes.value.findIndex((n) => n.id === selectedNode.value.id);

    if (nodeIndex !== -1) {
      const updatedNode = {
        ...nodes.value[nodeIndex],
        data: {
          ...nodes.value[nodeIndex].data,
          ...data,
        },
      };

      nodes.value[nodeIndex] = updatedNode;
      nodes.value = [...nodes.value];
      selectedNode.value = updatedNode;

      // If this is a source node with sample columns, propagate to connected transforms
      if (updatedNode.type === "source" && data.sampleColumns) {
        propagateColumnsToTransform(updatedNode.id, data.sampleColumns);
      }
    }
  }
};

const propagateColumnsToTransform = (sourceNodeId, columns) => {
  // Find edges from this source node
  const connectedEdges = edges.value.filter((e) => e.source === sourceNodeId);

  connectedEdges.forEach((edge) => {
    const transformIndex = nodes.value.findIndex(
      (n) => n.id === edge.target && n.type === "transform"
    );

    if (transformIndex !== -1) {
      // Create column mappings from source columns - using PascalCase to match backend
      // Handle both string columns and {name, type} objects
      const columnMappings = columns.map((col) => {
        const colName = typeof col === "string" ? col : col.name;
        const colType = typeof col === "string" ? "string" : col.type || "string";

        return {
          SourceColumn: colName,
          TargetColumn: colName,
          DataType: colType,
          Include: true,
        };
      });

      // Update transform node
      nodes.value[transformIndex] = {
        ...nodes.value[transformIndex],
        data: {
          ...nodes.value[transformIndex].data,
          columnMappings: columnMappings,
        },
      };
    }
  });

  // Force reactivity
  nodes.value = [...nodes.value];
};

const getConfigPanelComponent = (node) => {
  const components = {
    source: SourceConfigPanel,
    transform: TransformConfigPanel,
    destination: DestinationConfigPanel,
  };
  return components[node.type];
};

const getSourceNode = () => {
  return nodes.value.find((n) => n.type === "source") || null;
};

const getTransformNode = () => {
  return nodes.value.find((n) => n.type === "transform") || null;
};

const updatePipelineName = () => {
  emit("update-name", pipelineName.value);
};

const fitView = () => {
  vueFlowFitView({ padding: 0.2, duration: 300 });
};

const savePipeline = () => {
  // Convert nodes back to your backend format
  const sourceNode = nodes.value.find((n) => n.type === "source");
  const transformNode = nodes.value.find((n) => n.type === "transform");
  const destNode = nodes.value.find((n) => n.type === "destination");

  const pipelineData = {
    name: pipelineName.value,
    connectionId: sourceNode?.data?.connectionId,
    config: {
      extract: {
        connectionId: sourceNode?.data?.connectionId,
        endpoint: sourceNode?.data?.endpoint,
      },
      transform: {
        columnMappings: transformNode?.data?.columnMappings || [],
      },
      load: {
        tableName: destNode?.data?.tableName,
        syncMode: destNode?.data?.syncMode || "AppendAll",
        uniqueKeyColumns: destNode?.data?.uniqueKeyColumns || [],
        autoCreateTable: true,
        loadDestinationId: "00000000-0000-0000-0000-000000000000",
      },
    },
  };

  emit("save", pipelineData);
  isDirty.value = false;
};
</script>

<style>
@import "@vue-flow/core/dist/style.css";
@import "@vue-flow/core/dist/theme-default.css";
@import "@vue-flow/controls/dist/style.css";
@import "@vue-flow/minimap/dist/style.css";
</style>

<style scoped>
.pipeline-canvas-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 78px);
  background: #f8f9fa;
}

.canvas-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  z-index: 10;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.toolbar-left h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  display: none;
}

.pipeline-name-input {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  border: 2px solid transparent;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  background: transparent;
  transition: all 0.2s;
  min-width: 250px;
}

.pipeline-name-input:hover {
  background: rgba(0, 0, 0, 0.02);
}

.pipeline-name-input:focus {
  outline: none;
  border-color: var(--color-accent);
  background: white;
}

.toolbar-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.node-palette {
  display: flex;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.02);
  padding: 0.5rem;
  border-radius: 10px;
}

.palette-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: white;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;
}

.palette-btn:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.toolbar-right {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.tool-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background: white;
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;
}

.tool-btn:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
  transform: translateY(-1px);
}

.tool-btn.primary {
  background: var(--color-primary);
  color: white;
  border-color: transparent;
}

.tool-btn.primary:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.tool-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none !important;
}

.unsaved-badge {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-warning);
  background: var(--color-warning-bg);
  border: 1px solid var(--color-warning-light);
  padding: 0.2rem 0.6rem;
  border-radius: 99px;
  white-space: nowrap;
}

.vue-flow-wrapper {
  flex: 1;
  background: #f8f9fa;
}

/* Override Vue Flow default styles */
:deep(.vue-flow__background) {
  background-color: #f8f9fa;
}

:deep(.vue-flow__edge-path) {
  stroke: #667eea;
  stroke-width: 2;
}

:deep(.vue-flow__edge.selected .vue-flow__edge-path) {
  stroke: var(--color-primary);
  stroke-width: 3;
}

:deep(.vue-flow__controls) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

:deep(.vue-flow__controls-button) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: white;
  transition: all 0.2s;
}

:deep(.vue-flow__controls-button:hover) {
  background: var(--color-accent);
  color: white;
}

:deep(.vue-flow__minimap) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

/* Panel slide animation */
.slide-panel-enter-active,
.slide-panel-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-panel-enter-from,
.slide-panel-leave-to {
  transform: translateX(100%);
}
</style>
